<template>
  <TransitionRoot appear :show="show" as="template">
    <Dialog as="div" class="relative z-10" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
            >
              <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">
                Create new instance
              </DialogTitle>
              <div class="mt-2">
                <p class="text-sm text-gray-500">This is visible to Agentnoon Admins Only.</p>
                <div
                  class="mt-4 rounded-md px-3 pt-2.5 pb-1.5 shadow-xs ring-1 ring-gray-300 ring-inset focus-within:ring-2 focus-within:ring-blue-600"
                >
                  <label for="name" class="block text-xs font-medium text-gray-900">
                    Company name
                  </label>
                  <input
                    id="name"
                    v-model="companyName"
                    type="text"
                    name="name"
                    class="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    placeholder="Acme Corp."
                  />
                </div>

                <div
                  v-if="isAgentnoonUser"
                  class="mt-3 rounded-md px-3 pt-2.5 pb-1.5 shadow-xs ring-1 ring-gray-300 ring-inset focus-within:ring-2 focus-within:ring-blue-600"
                >
                  <label for="region" class="block text-xs font-medium text-gray-900">
                    Region (Org account location)
                  </label>
                  <select
                    id="region"
                    v-model="region"
                    class="block w-full border-0 p-0 text-gray-900 focus:ring-0 sm:text-sm sm:leading-6"
                  >
                    <option v-for="key in allowedRegionKeys" :key="key" :value="key">
                      {{ regionLabel(key) }}
                    </option>
                  </select>
                  <p class="mt-1 text-[11px] text-gray-500">
                    Will create on
                    <span class="font-medium">{{ targetApiBase }}</span>
                  </p>
                </div>
              </div>

              <div class="mt-4 flex justify-end">
                <button
                  type="button"
                  class="bg-white-100 text-black-900 focus-visible:black-500 hover:bg-black-300 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2"
                  @click="$emit('close')"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isCreateDisabled"
                  @click="_createBoard"
                >
                  Create
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'

import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { createBoard, createBoardInRegion } from '@/services/boards-api.service'

import { useRouter } from 'vue-router'
import { createBoardId } from '@/lib/idCreator'
import { REGIONS, setPersistedRegion } from '@/services/region.service'
import useToast from '@/hooks/use-toast'
import { useStore } from 'vuex'
import { getAllowedEndpoints } from '@/services/api.service'
import { isSuperAdminUser } from '@/utils/admin-users'
import { logger } from '@/utils/logger'

const { push } = useRouter()
const store = useStore()
const { queueToast } = useToast()

const companyName = ref('')
const allowedRegionKeys = ref([])
const endpoints = ref(new Map())
const region = ref('us')
const regionLabel = (key) => {
  if (key === 'staging') return 'Staging'
  if (key === 'preview') return 'Preview'
  if (key === 'local') return 'Local'
  return REGIONS[key] || key
}
let map = null
onMounted(async () => {
  map = await getAllowedEndpoints()
  endpoints.value = map
  allowedRegionKeys.value = Array.from(map.keys())
  const preferred = store.getters['region/selectedRegion'] || 'us'
  region.value = allowedRegionKeys.value.includes(preferred)
    ? preferred
    : allowedRegionKeys.value[0]
})

const targetApiBase = computed(() => {
  if (!map) return ''
  const key = (region.value || '').toLowerCase()
  return map.get(key)
})

const isAgentnoonUser = computed(() => isSuperAdminUser(store.getters.myEmail))
const isCreateDisabled = computed(() => !companyName.value?.trim())
defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

defineEmits(['close'])

const _createBoard = async () => {
  logger.info('create board')

  const boardName = companyName.value
  const _companyName = companyName.value
  const newBoardId = createBoardId()

  const payload = {
    boardId: newBoardId,
    boardName,
    companyName: _companyName
  }

  const ret = isAgentnoonUser.value
    ? await createBoardInRegion({ payload, region: region.value })
    : await createBoard({ payload })

  if (ret?.boardId) {
    queueToast('Board created successfully', {
      timeout: 1200,
      position: 'top',
      bg: 'bg-success-50'
    })
    setPersistedRegion(region.value)
    await push({ name: 'Landing', params: { boardId: ret.boardId } })
    setTimeout(() => window.location.reload(), 800)
  } else {
    queueToast('Failed to create board', {
      timeout: 3000,
      position: 'top',
      bg: 'bg-error-50'
    })
  }
}
</script>
